import { kundaliRegionalLocale, type KundaliRegionalLocale } from "@/i18n/regional-locales";

import {
  REGIONAL_KUNDALI_FORM_COPY,
  type RegionalSubmitErrorKey,
} from "./KundaliFormCard.i18n.regional";
import type { KundaliTranslations } from "./KundaliFormCard.i18n.types";

/** The language the kundali API, analytics and chat receive. */
export type KundaliLang = "en" | "hi";

/**
 * The language the seeker READS: `KundaliLang`, or an in-language regional
 * edition. Display only — a regional code never leaves the client.
 */
export type KundaliLocale = KundaliLang | KundaliRegionalLocale;

export type { KundaliTranslations } from "./KundaliFormCard.i18n.types";

const DEFAULT_TRANSLATIONS: Record<KundaliLang, KundaliTranslations> = {
  en: {
    name: "Your Name",
    birthDate: "Birth Date",
    birthTime: "Birth Time",
    birthPlace: "Birth Place",
    optional: "(optional)",
    timeUnknownToggle: "Don't know your birth time?",
    timeUnknownExplainer:
      "No problem — we'll provide a reduced, time-insensitive reading from your birth date and place. Without your exact birth time, we won't claim an exact Lagna, houses, divisional charts, or dasha timing.",
    submit: "Generate Free Kundali →",
    namePlaceholder: "Enter your full name",
    placePlaceholder: "City, State (e.g. Chennai, Tamil Nadu)",
    savedNote: "Your reading is saved securely so you can access it anytime.",
    loading: "Calculating your stars...",
    phone: "WhatsApp mobile number",
    phonePlaceholder: "98765 43210",
    phoneCountryLabel: "Country code",
    phoneReason:
      "Optional. Add it to save your Kundali and reopen it later. It does not opt you into marketing.",
    email: "Email",
    emailPlaceholder: "you@example.com (optional)",
    emailToggle: "Add an email backup (optional)",
    placeChange: "Change",
    placeDidYouMean: "Did you mean",
    retryTitle: "Couldn't reach the stars just now",
    retryBody: "Your details are saved. The connection slipped — tap to try once more.",
    retryAction: "Try again",
  },
  // Native Hinglish (Roman) — the verified brand register (rule 21: single Indian noun
  // + English action word; pure Devanagari compounds score 0). These labels were
  // Devanagari while this same form's error messages were already Hinglish — a register
  // split inside one form, on the exact surface where the plan's #1 failure class
  // concentrates (birthPlace). The Devanagari place-hint reads in a script the brand
  // register says converts ~0. scripts/check_hindi_register.py blocks Devanagari here.
  hi: {
    name: "Aapka naam",
    birthDate: "Janm tithi",
    birthTime: "Janm samay",
    birthPlace: "Janm sthal",
    optional: "(optional)",
    timeUnknownToggle: "Janm samay nahi pata?",
    timeUnknownExplainer:
      "Koi baat nahi — janm tithi aur sthal se sirf reduced, time-insensitive reading show karenge. Exact janm samay ke bina exact Lagna, bhaav, divisional charts ya dasha timing claim nahi karenge.",
    submit: "Free Kundali banayein →",
    namePlaceholder: "Apna pura naam likhein",
    placePlaceholder: "Sheher, state (jaise Chennai, Tamil Nadu)",
    savedNote: "Aapki kundali securely save hoti hai, kabhi bhi dekh sakte hain.",
    loading: "Aapke graha calculate ho rahe hain...",
    phone: "WhatsApp mobile number",
    phonePlaceholder: "98765 43210",
    phoneCountryLabel: "Country code",
    phoneReason:
      "Optional. Kundali save karke baad mein kholne ke liye add karein. Marketing opt-in alag hota hai.",
    email: "Email",
    emailPlaceholder: "you@example.com (optional)",
    emailToggle: "Email backup add karein (optional)",
    placeChange: "Badlein",
    placeDidYouMean: "Kya aapka matlab tha",
    retryTitle: "Abhi taaron tak nahi pahunch paaye",
    retryBody: "Aapki details safe hain. Connection toot gaya — ek baar phir try karein.",
    retryAction: "Phir try karein",
  },
};

export function getKundaliTranslations(
  language: KundaliLocale,
  overrides: {
    submitLabel?: string;
    savedNote?: string;
    loadingLabel?: string;
    namePlaceholder?: string;
    placePlaceholder?: string;
    phoneReason?: string;
  } = {}
): KundaliTranslations {
  const regional = kundaliRegionalLocale(language);
  const defaults = regional
    ? REGIONAL_KUNDALI_FORM_COPY[regional].fields
    : DEFAULT_TRANSLATIONS[language as KundaliLang];
  return {
    ...defaults,
    submit: overrides.submitLabel || defaults.submit,
    namePlaceholder: overrides.namePlaceholder || defaults.namePlaceholder,
    placePlaceholder: overrides.placePlaceholder || defaults.placePlaceholder,
    phoneReason: overrides.phoneReason || defaults.phoneReason,
    savedNote: overrides.savedNote || defaults.savedNote,
    loading: overrides.loadingLabel || defaults.loading,
  };
}

// Submit failures are thrown and tracked in English (analytics error keys are
// derived from the English text); only what the seeker reads is localized.
export const SUBMIT_ERROR_MESSAGES = {
  chartIncomplete: "Your complete chart could not be prepared. Please try again.",
  invalidPhone: "Enter a valid mobile number.",
  generic: "Something went wrong",
  checkForm: "Please check the form.",
} as const;

const HINGLISH_SUBMIT_ERRORS: Record<string, string> = {
  [SUBMIT_ERROR_MESSAGES.chartIncomplete]:
    "Aapki poori kundali abhi taiyaar nahi ho payi. Kripya dobara try karein.",
  [SUBMIT_ERROR_MESSAGES.invalidPhone]: "Sahi mobile number likhein.",
  [SUBMIT_ERROR_MESSAGES.checkForm]: "Form ki details check karein.",
};

const REGIONAL_SUBMIT_ERROR_KEYS: Readonly<Record<string, RegionalSubmitErrorKey>> = {
  [SUBMIT_ERROR_MESSAGES.chartIncomplete]: "chartIncomplete",
  [SUBMIT_ERROR_MESSAGES.invalidPhone]: "invalidPhone",
  [SUBMIT_ERROR_MESSAGES.checkForm]: "checkForm",
};

/**
 * The submit error a seeker reads: English stays as-is; Hindi and the regional
 * editions never show raw API text.
 */
export function displaySubmitError(message: string, language: KundaliLocale): string {
  if (language === "en") return message;
  const regional = kundaliRegionalLocale(language);
  if (regional) {
    const key = REGIONAL_SUBMIT_ERROR_KEYS[message] ?? "generic";
    return REGIONAL_KUNDALI_FORM_COPY[regional].submitErrors[key];
  }
  return HINGLISH_SUBMIT_ERRORS[message] ?? "Kuch gadbad ho gayi. Kripya dobara try karein.";
}

// The intent banner above the form (a seeker arriving from a report CTA or with
// a question). Hinglish on the Hindi form, like the rest of this file.
export const INTENT_BANNER_COPY = {
  en: {
    unlocking: (name: string, price: number) => `Unlocking · ${name} · ₹${price}`,
    reportBody: (name: string, price: number) =>
      `Enter your birth details below — I build your chart first, then your ${name} (₹${price}) opens right here. No separate page, no waiting.`,
    questionEyebrow: "Pandit-ji · Your question",
    questionBody:
      "I'll read your chart to answer this. Share your birth details below — your question stays open and I'll continue it the moment your kundali is ready.",
  },
  hi: {
    unlocking: (name: string, price: number) => `Unlock ho raha hai · ${name} · ₹${price}`,
    reportBody: (name: string, price: number) =>
      `Neeche janm details bharein — pehle aapki kundali banegi, phir aapki ${name} (₹${price}) yahin khulegi. Alag page nahi, koi intezaar nahi.`,
    questionEyebrow: "Pandit-ji · Aapka sawal",
    questionBody:
      "Is sawal ka jawab dene ke liye main aapki kundali padhunga. Neeche janm details bharein — aapka sawal khula rahega aur kundali bante hi main jawab dunga.",
  },
} as const;
